import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Clock, ListChecks, Pencil, Award, Calendar, Brain, Play, AlertCircle } from "lucide-react";

export default function ContestDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [contest, setContest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/contests/${id}`).then((r) => { setContest(r.data); setLoading(false); })
            .catch(() => { setError("Không tìm thấy cuộc thi"); setLoading(false); });
    }, [id]);

    const startExam = async () => {
        if (!user) {
            navigate("/dang-nhap", { state: { from: `/cuoc-thi/${id}/lam-bai` } });
            return;
        }
        setStarting(true); setError("");
        try {
            await api.post(`/exam/start/${id}`);
            navigate(`/cuoc-thi/${id}/lam-bai`);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail));
        } finally {
            setStarting(false);
        }
    };

    if (loading) return <main className="pt-32 container-x"><div className="h-64 card shimmer" /></main>;
    if (!contest) return <main className="pt-32 container-x text-center"><h2>Không tìm thấy cuộc thi</h2></main>;

    return (
        <main className="pt-24 pb-12 min-h-screen">
            {/* Cover */}
            <section className="relative h-80 overflow-hidden">
                {contest.cover_image && <img src={contest.cover_image} className="absolute inset-0 w-full h-full object-cover opacity-30" alt="" />}
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B0A1C]/50 via-[#0B0A1C]/70 to-[var(--navy-deep)]" />
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="relative container-x h-full flex items-end pb-10">
                    <div className="space-y-4 max-w-3xl fade-up">
                        <div className="flex gap-2">
                            <span className="badge badge-gold">{contest.organizer}</span>
                            <span className="badge badge-gold">Đang mở</span>
                        </div>
                        <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight">{contest.title}</h1>
                    </div>
                </div>
            </section>

            <section className="container-x py-12 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="card p-7 fade-up">
                        <h2 className="font-heading text-xl font-semibold mb-4">Giới thiệu cuộc thi</h2>
                        <p className="text-slate-600 leading-relaxed whitespace-pre-line">{contest.description}</p>
                    </div>

                    {contest.has_essay && contest.essay_prompt && (
                        <div className="card p-7 fade-up delay-1">
                            <div className="flex items-center gap-2 mb-3">
                                <Pencil className="w-5 h-5 text-[var(--gold-deep)]" />
                                <h3 className="font-heading text-lg font-semibold">Đề tự luận</h3>
                            </div>
                            <p className="text-slate-600 leading-relaxed italic">"{contest.essay_prompt}"</p>
                            {contest.essay_rubric && (
                                <div className="mt-4 pt-4 border-t border-[var(--border)]">
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Rubric chấm điểm</div>
                                    <p className="text-sm text-slate-500">{contest.essay_rubric}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="card p-7 fade-up delay-2">
                        <div className="flex items-center gap-2 mb-4">
                            <Brain className="w-5 h-5 text-[var(--gold-deep)]" />
                            <h3 className="font-heading text-lg font-semibold">AI hỗ trợ chấm thi</h3>
                        </div>
                        <p className="text-sm text-slate-500 mb-3">Bài tự luận của bạn sẽ được hệ thống AI <span className="text-[var(--gold-deep)] font-semibold">{providerName(contest.ai_provider)}</span> chấm sơ bộ.</p>
                        <ul className="space-y-2 text-sm text-slate-600">
                            <li className="flex gap-2"><span className="text-[var(--gold-deep)]">▸</span> Đánh giá theo 4 tiêu chí: Liên quan – Kiến thức – Lập luận – Truyền thông</li>
                            <li className="flex gap-2"><span className="text-[var(--gold-deep)]">▸</span> Sinh feedback chi tiết + độ tin cậy</li>
                            <li className="flex gap-2"><span className="text-[var(--gold-deep)]">▸</span> Ban Giám khảo có quyền phủ quyết và điều chỉnh điểm cuối</li>
                        </ul>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="card p-6 sticky top-24 fade-up delay-1">
                        <div className="space-y-4">
                            <Info icon={<Clock />} label="Thời lượng" value={`${contest.duration_minutes} phút`} />
                            <Info icon={<ListChecks />} label="Trắc nghiệm" value={`${contest.num_mcq} câu`} />
                            <Info icon={<Pencil />} label="Tự luận" value={contest.has_essay ? "1 bài" : "Không có"} />
                            <Info icon={<Award />} label="Bình chọn" value={contest.voting_enabled ? "Có" : "Không"} />
                            <Info icon={<Calendar />} label="Kết thúc" value={new Date(contest.end_at).toLocaleDateString("vi-VN")} />
                        </div>

                        {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg p-3 mt-4 flex gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}</div>}

                        <button onClick={startExam} disabled={starting} className="btn-primary w-full mt-6" data-testid="start-exam-btn">
                            <Play className="w-4 h-4" /> {starting ? "Đang khởi tạo…" : "Bắt đầu thi"}
                        </button>

                        <Link to={`/bang-xep-hang/${contest.id}`} className="btn-secondary w-full mt-3" data-testid="see-leaderboard">
                            <Award className="w-4 h-4" /> Xem bảng xếp hạng
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}

function Info({ icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center text-[var(--gold-deep)] [&_svg]:w-4 [&_svg]:h-4">{icon}</div>
            <div>
                <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
                <div className="text-sm text-[var(--navy)] font-semibold">{value}</div>
            </div>
        </div>
    );
}

function providerName(p) {
    return { minimax: "Minimax 2.7", openrouter_gpt5: "GPT-5", openrouter_gemini3: "Gemini 3 Pro" }[p] || p;
}
